import type { TrackingCallback } from '@growthbook/growthbook'
import { GrowthBook } from '@growthbook/growthbook'

import { DEFAULT_LOAD_TIMEOUT_MS, FEATURE_FLAGS_API_HOST } from './constants'
import type { FeatureFlagsConfig, FlagAttributes } from './types'

// Internal module: the only place (with server.ts) that knows the vendor.
// Nothing here is re-exported from the public barrels.

type AttributeInput = Pick<
  FeatureFlagsConfig,
  'applicationId' | 'attributes' | 'identity'
>

const RESERVED_KEYS = new Set(['id', 'applicationId'])

/**
 * `id` is withheld until the identity is resolved: GrowthBook skips an
 * experiment whose hash attribute is missing, and `'id' in getAttributes()` is
 * how readiness tells "not yet" from "no consent" (`id: null`).
 */
export const resolveAttributes = ({
  applicationId,
  attributes = {},
  identity,
}: AttributeInput): FlagAttributes => {
  const consumer = Object.fromEntries(
    Object.entries(attributes).filter(([key]) => !RESERVED_KEYS.has(key)),
  )
  return {
    ...consumer,
    applicationId,
    ...(identity?.isResolved && { id: identity.anonymousId }),
  }
}

/**
 * A new instance owned by its caller (a provider mount or a client), built
 * with that caller's attributes and destroyed with it. Instances are never
 * shared: a reused one would render its previous owner's attributes for one
 * commit, and one owner's `destroy()` would freeze every other. Remounts stay
 * cheap because the SDK caches payloads per client key at module level.
 */
export const createGrowthBook = (
  config: FeatureFlagsConfig,
  trackingCallback: TrackingCallback,
): GrowthBook =>
  new GrowthBook({
    apiHost: FEATURE_FLAGS_API_HOST,
    clientKey: config.clientKey || undefined,
    attributes: resolveAttributes(config),
    enableDevMode: config.environment !== 'prd',
    trackingCallback,
  })

const loads = new WeakMap<GrowthBook, Promise<void>>()

/**
 * Loads the payload once per instance. Settles on success, timeout, network
 * error or a missing client key, so a blocked CDN leaves every flag on its
 * default instead of throwing.
 */
export const loadFeatures = (
  growthbook: GrowthBook,
  { streaming = true }: { streaming?: boolean } = {},
): Promise<void> => {
  const pending = loads.get(growthbook)
  if (pending) return pending

  // Dev mode points the devtools extension at `window._growthbook` from the
  // constructor, so any other SDK instance on the page (e.g. an embedded
  // package) that is constructed later steals it. Re-claim it when loading.
  if (typeof window !== 'undefined' && growthbook.inDevMode()) {
    ;(window as { _growthbook?: GrowthBook })._growthbook = growthbook
  }

  // Without a key, an empty payload marks the instance ready (and re-renders
  // React consumers) so a ready gate never waits on a request that can't happen.
  const request: Promise<unknown> = growthbook.getClientKey()
    ? growthbook.init({ streaming, timeout: DEFAULT_LOAD_TIMEOUT_MS })
    : growthbook.setPayload({})
  const loading = request.then(
    () => undefined,
    () => undefined,
  )

  loads.set(growthbook, loading)
  return loading
}
